"use client";

import type { FestivalDetails, ProcessedSet } from "@workspace/types";
import { memo } from "react";
import { DaySelector } from "@/components/features/festival/navigation/day-selector";
import { TimeNavigation } from "@/components/features/festival/tabs/time/time-navigation";
import {
	ReviewFilter,
	type ReviewFilterType,
} from "@/components/features/profile/filters/review-filter";
import {
	BookmarkFilter,
	type BookmarkFilters,
} from "@/components/shared/filters/bookmark/bookmark-filter";
import { LayoutToggle } from "@/components/shared/filters/core/layout-toggle";
import { EnhancedSearchInput } from "@/components/shared/filters/search/enhanced-search-input";
import { SortToggle } from "@/components/shared/filters/core/sort-toggle";
import { GenreFilter } from "@/components/shared/filters/genre/genre-filter";
import {
	ARTIST_SORT_OPTIONS,
	FESTIVAL_SORT_OPTIONS,
	UniversalSortDropdown,
} from "@/components/shared/filters/search/universal-sort-dropdown";
import { StageFilter } from "@/components/shared/filters/stage-filter";
import { useFilteredStages } from "@/hooks/features/festival/use-filtered-stages";
import { isLineupOnlyFestival } from "@/lib/features/festival/festival-status";
import { cn } from "@/lib/utils";

interface FestivalFilterControlsProps {
	activeTab: string;
	festivalDetails: FestivalDetails;
	availableDays: string[];
	selectedDay: string;
	handleDayChange: (day: string) => void;
	getDayCount: (day: string) => number;
	currentBookmarkFilters: BookmarkFilters;
	handleBookmarkFiltersChange: (filters: BookmarkFilters) => void;
	isHorizontalLayout: boolean;
	handleLayoutToggle: () => void;
	sortAscending: boolean;
	handleSortToggle: () => void;
	processedSets: ProcessedSet[];
	isLoading: boolean;
	selectedStage: string;
	handleStageChange: (stage: string) => void;
	dbStagesList: { id: string; name: string; position: number }[];
	selectedGenre: string;
	handleGenreChange: (genre: string) => void;
	allGenres: string[];
	hasNoGenreArtists: boolean;
	searchTerm: string;
	handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	artistsSort?: string;
	handleArtistsSortChange?: (sort: string) => void;
	user?: { id: string } | null;
	myListDay: string;
	handleMyListDayChange: (day: string) => void;
	applyReviewFilter: (sets: ProcessedSet[]) => ProcessedSet[];
	reviewFilter: string;
	handleReviewFilterChange: (filter: string) => void;
	myListSort: string;
	handleMyListSortChange: (sort: string) => void;
	calendarPreferences?: import("@workspace/types/src/entities/user").CalendarViewPreferences;
	onCalendarPreferencesSaved?: (
		preferences: import("@workspace/types/src/entities/user").CalendarViewPreferences,
	) => void;
	isOwnProfile?: boolean;
}

export const FestivalFilterControls = memo(
	function FestivalFilterControlsComponent({
		activeTab,
		festivalDetails,
		availableDays,
		selectedDay,
		handleDayChange,
		getDayCount,
		currentBookmarkFilters,
		handleBookmarkFiltersChange,
		isHorizontalLayout,
		handleLayoutToggle,
		sortAscending,
		handleSortToggle,
		processedSets,
		isLoading,
		selectedStage,
		handleStageChange,
		dbStagesList,
		selectedGenre,
		handleGenreChange,
		allGenres,
		hasNoGenreArtists,
		searchTerm,
		handleSearchChange,
		artistsSort = "popularity",
		handleArtistsSortChange,
		user,
		myListDay,
		handleMyListDayChange,
		applyReviewFilter,
		reviewFilter,
		handleReviewFilterChange,
		myListSort,
		handleMyListSortChange,
		calendarPreferences,
		onCalendarPreferencesSaved,
		isOwnProfile = true,
	}: FestivalFilterControlsProps) {
		const showFilters =
			activeTab === "calendar" ||
			activeTab === "time" ||
			activeTab === "stages" ||
			activeTab === "artists" ||
			activeTab === "reviews";

		// Get filtered stages (always call the hook to maintain hook order)
		const filteredStages = useFilteredStages({
			dbStagesList,
			processedSets,
			selectedDay,
			selectedStage,
			handleStageChange,
		});

		// For stages tab, show all stages regardless of whether they have sets
		// This provides a consistent list for users to choose from
		const stagesToShow = activeTab === "stages" ? dbStagesList : filteredStages;

		if (!showFilters) return null;

		return (
			<div
				className={cn(
					"sticky top-[3.5rem] z-30 bg-background py-3 md:top-[calc(3.5rem+env(safe-area-inset-top))]",
				)}
			>
				{/* Calendar filters */}
				{activeTab === "calendar" &&
					!isLineupOnlyFestival(festivalDetails) &&
					availableDays.length > 0 && (
						<div className="flex items-center justify-between">
							<DaySelector
								availableDays={availableDays}
								selectedDay={selectedDay}
								onDayChange={handleDayChange}
								getDayCount={getDayCount}
							/>
							<div className="flex flex-row gap-2 pl-2">
								<BookmarkFilter
									filters={currentBookmarkFilters}
									onFiltersChange={handleBookmarkFiltersChange}
									title="Filter calendar sets"
									hideFollowing={!isOwnProfile}
									calendarPreferences={calendarPreferences}
									onCalendarPreferencesSaved={onCalendarPreferencesSaved}
								/>
								<LayoutToggle
									isHorizontal={isHorizontalLayout}
									onClick={handleLayoutToggle}
								/>
								<SortToggle
									ascending={sortAscending}
									onClick={handleSortToggle}
									isHorizontal={isHorizontalLayout}
								/>
							</div>
						</div>
					)}

				{/* Time filters */}
				{activeTab === "time" &&
					!isLineupOnlyFestival(festivalDetails) &&
					availableDays.length > 0 && (
						<div className="space-y-3">
							<div className="flex items-center justify-between">
								<DaySelector
									availableDays={availableDays}
									selectedDay={selectedDay}
									onDayChange={handleDayChange}
									getDayCount={getDayCount}
								/>
								<div className="flex flex-row gap-2 pl-2">
									<BookmarkFilter
										filters={currentBookmarkFilters}
										onFiltersChange={handleBookmarkFiltersChange}
										title="Filter time view sets"
										hideFollowing={!isOwnProfile}
										calendarPreferences={calendarPreferences}
										onCalendarPreferencesSaved={onCalendarPreferencesSaved}
									/>
									<SortToggle
										ascending={sortAscending}
										onClick={handleSortToggle}
									/>
								</div>
							</div>
							{!isLoading && processedSets.length > 0 && (
								<TimeNavigation
									sets={processedSets}
									selectedDay={selectedDay}
									sortAscending={sortAscending}
									festivalDetails={festivalDetails}
								/>
							)}
						</div>
					)}

				{/* Stages filters */}
				{activeTab === "stages" &&
					!isLineupOnlyFestival(festivalDetails) &&
					availableDays.length > 0 && (
						<div className="space-y-3">
							<div className="flex items-center justify-between">
								<DaySelector
									availableDays={availableDays}
									selectedDay={selectedDay}
									onDayChange={handleDayChange}
									getDayCount={getDayCount}
								/>
								<div className="flex flex-row gap-2 pl-2">
									<BookmarkFilter
										filters={currentBookmarkFilters}
										onFiltersChange={handleBookmarkFiltersChange}
										title="Filter stage sets"
										hideFollowing={!isOwnProfile}
										calendarPreferences={calendarPreferences}
										onCalendarPreferencesSaved={onCalendarPreferencesSaved}
									/>
									<SortToggle
										ascending={sortAscending}
										onClick={handleSortToggle}
									/>
								</div>
							</div>
							<div className="flex items-center justify-between">
								<div className="flex-1">
									<StageFilter
										value={selectedStage}
										onValueChange={handleStageChange}
										stages={stagesToShow}
									/>
								</div>
							</div>
						</div>
					)}

				{/* Artists filters */}
				{activeTab === "artists" && (
					<div className="space-y-3">
						<div className="flex gap-3">
							<div className="flex-1">
								<GenreFilter
									value={selectedGenre}
									onValueChange={handleGenreChange}
									availableGenres={allGenres}
									hasNoGenreArtists={hasNoGenreArtists}
									placeholder="All Genres"
								/>
							</div>
							<div className="flex-1">
								<UniversalSortDropdown
									value={artistsSort}
									onValueChange={handleArtistsSortChange || (() => {})}
									options={ARTIST_SORT_OPTIONS}
									preserveScrollPosition={true}
								/>
							</div>
						</div>
						<div className="flex items-center justify-between">
							<EnhancedSearchInput
								value={searchTerm}
								onValueChange={(value) => {
									handleSearchChange({
										target: { value },
									} as React.ChangeEvent<HTMLInputElement>);
								}}
								placeholder="Search artists..."
								className="flex-1"
								showLoadingIndicator={true}
								isLoading={isLoading}
							/>
							<div className="flex flex-row gap-2 pl-2">
								<BookmarkFilter
									filters={currentBookmarkFilters}
									onFiltersChange={handleBookmarkFiltersChange}
									title="Filter artist sets"
									hideFollowing={!isOwnProfile}
									calendarPreferences={calendarPreferences}
									onCalendarPreferencesSaved={onCalendarPreferencesSaved}
									className="h-10 w-10"
								/>
							</div>
						</div>
					</div>
				)}

				{/* Reviews filters - Show for logged-in users OR when viewing someone else's profile */}
				{activeTab === "reviews" && (user || !isOwnProfile) && (
					<div className="flex flex-col gap-3">
						<DaySelector
							availableDays={availableDays}
							selectedDay={myListDay}
							onDayChange={handleMyListDayChange}
							getDayCount={(day) => {
								const filteredSets = applyReviewFilter(processedSets);
								return day === "total"
									? filteredSets.length
									: filteredSets.filter((set) => set.day === day).length;
							}}
							includeAllOption={!isLineupOnlyFestival(festivalDetails || {})}
							allOptionLabel="All"
							className="w-full"
						/>
						<div className="flex w-full flex-row gap-3">
							<div className="flex-1">
								<ReviewFilter
									value={reviewFilter as ReviewFilterType}
									onValueChange={handleReviewFilterChange}
								/>
							</div>
							<div className="flex-1">
								<UniversalSortDropdown
									value={myListSort}
									onValueChange={handleMyListSortChange}
									options={
										isLineupOnlyFestival(festivalDetails || {})
											? FESTIVAL_SORT_OPTIONS.filter(
													(opt) => !opt.value.startsWith("time"),
												)
											: FESTIVAL_SORT_OPTIONS
									}
								/>
							</div>
						</div>
					</div>
				)}
			</div>
		);
	},
);
